import React, { useContext } from 'react'
import Input from '../inputs/Input'
import Select from '../inputs/Select'
import FormGrid from '../layouts/FormGrid'
import Label from '../inputs/Label'
import InputWrap from '../inputs/InputWrap'
import InputGroupTitle from '../inputs/InputGroupTitle'
import PageTitle from '../layouts/PageTitle'
import DateInput from '../inputs/DateInput'
import TextareaInput from '../inputs/TextareaInput'
import CheckboxInput from '../inputs/CheckboxInput'
import useSteps from '../../hooks/useSteps'
import FileInput from '../inputs/FileInput'
import NextStepButton from '../utils/NextStepButton'
import SelectAllCities from '../inputs/SelectAllCities'
import CheckboxAccordion from '../inputs/CheckboxAccordion'
import useInput from '../../hooks/useInput'
import { AdOrderContext } from '../../contexts/AdOrder'
import { getFieldConfig, getNestedFieldConfig } from '../../utils/FieldSettings'

export default function AdInformation({ adRestImageUrl, cities = [] }) {
  const { nextStep } = useSteps()
  const { getValue } = useInput()
  const { fieldSettings } = useContext(AdOrderContext)
  const { adOrder } = React.useContext(AdOrderContext)

  const assignmentName = getFieldConfig(fieldSettings, 'assignment_name', {
    title: 'Opgavens navn',
    placeholder: 'Fritekst',
    required: true,
  })

  const dateToFrom = getFieldConfig(fieldSettings, 'date_to_from', {
    title: 'Vælg dato til og fra',
    text: '<p>Vælg, hvornår din annonce skal kunne ses på skærmene</p>',
    placeholder_startdate: 'Vælg startdato',
    placeholder_enddate: 'Vælg periode',
    required: true,
  })

  const cityportals = getFieldConfig(fieldSettings, 'cityportals', {
    title: 'Hvilke byportaler vil du have annoncen på?',
    text: '<p>For hver valgte byportaler opkræves 250 kr eksl. moms...</p>',
    required: true,
  })

  const deliverAdd = getFieldConfig(fieldSettings, 'deliver_add', {
    title: 'Leverer I selv annoncen?',
    text: '<p>Find de tekniske krav for annoncer her</p>',
    fields: {},
    required: true,
  })

  const image = getFieldConfig(fieldSettings, 'image', {
    title: 'Upload billede',
    placeholder: 'Slip fil her eller',
    text: '<p>Her kan du vedhæfte en billedfil, hvis du har et billede, du gerne vil have skal bruges til annoncen. OBS på filkrav: Filformat: jpg eller png.</p>',
    required: true,
  })

  const headline = getNestedFieldConfig(fieldSettings, 'deliver_add', 'headline', {
    title: 'Overskrift på annoncen',
    placeholder: 'Fritekst',
    text: 'Maks. 500 tegn',
  })

  const dateTimePlace = getNestedFieldConfig(fieldSettings, 'deliver_add', 'date_time_and_place', {
    title: 'Dato, tid og sted for begivenheden',
    placeholder: 'Fritekst',
    text: '',
  })

  const websiteLink = getNestedFieldConfig(fieldSettings, 'deliver_add', 'website_link', {
    title: 'Link til hjemmesiden',
    placeholder: 'https://',
    text: '',
  })

  const textForAdd = getNestedFieldConfig(fieldSettings, 'deliver_add', 'text_for_add', {
    title: 'Tekst til annoncen og evt. øvrige bemærkninger',
    placeholder: 'Fritekst',
    text: '',
  })
  
  const generateWeekOptions = (maxWeeks) => {
    const options = []
    for (let i = 1; i <= parseInt(maxWeeks); i++) {
      options.push({
        value: i,
        label: `${i} uge${i > 1 ? 'r' : ''}`
      })
    }
    return options
  }

  const minimumDate = () => {
    const d = new Date();
    d.setDate(d.getDate() + Number(dateToFrom.min_days ?? 1));

    return d;
  }

  const getFirstAvailableDate = () => {
    const date = minimumDate()
    const days = dateToFrom.days

    for (let i = 0; i < 30; i++) {
      const checkDate = new Date()
      checkDate.setDate(date.getDate() + i)

      const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday']
      const dayOfWeek = dayNames[checkDate.getDay()]

      if (days[dayOfWeek]) {
        return checkDate
      }
    }

    return null
  }

  return (
    <>
      <PageTitle step={0} />

      <FormGrid>
        <Input
          name={'name'}
          label={assignmentName.title}
          placeholder={assignmentName.placeholder}
          required={assignmentName.required}
        />

        <InputWrap>
          <InputGroupTitle>
            <Label required={dateToFrom.required}>{dateToFrom.title}</Label>
            <div dangerouslySetInnerHTML={{ __html: dateToFrom.text }} />
          </InputGroupTitle>
          <div className="date-from-to-grid">
            <FormGrid>
              <DateInput
                  name={'date_from'}
                  required={dateToFrom.required}
                  placeholder={dateToFrom.placeholder_startdate}
                  size={'half'}
                  updateWeeks
                  updatePrice
                  defaultValue={getFirstAvailableDate()}
                  minDate={minimumDate()}
                  allowedDays={dateToFrom.days}
                  label={dateToFrom.placeholder_startdate}
              />
              <Select
                  name="date_to"
                  placeholder={dateToFrom.placeholder_enddate}
                  required={dateToFrom.required}
                  size={'half'}
                  options={generateWeekOptions(dateToFrom.weeks)}
                  label={dateToFrom.placeholder_enddate}
              />
            </FormGrid>
          </div>
        </InputWrap>

        <InputWrap>
          <InputGroupTitle>
            <Label required={cityportals.required}>{cityportals.title}</Label>
            <div dangerouslySetInnerHTML={{ __html: cityportals.text }} />
          </InputGroupTitle>
          <FormGrid>
            <InputWrap>
              <div className={'select-all-cities'}>
                <SelectAllCities cities={[...cities]} />
              </div>
            </InputWrap>
          </FormGrid>
          <CheckboxAccordion height={152} showText={'Vis alle byer'} hideText={'Vis færre byer'}>
            <FormGrid>
              {cities.map((city, i) => (
                <InputWrap key={i} size={'third'}>
                  <CheckboxInput name={'cities'} label={city} value={city} multiple />
                </InputWrap>
              ))}
            </FormGrid>
          </CheckboxAccordion>
        </InputWrap>

        <InputWrap>
          <InputGroupTitle>
            <Label required={deliverAdd.required} extraText={deliverAdd.text}>
              {deliverAdd.title}
            </Label>
          </InputGroupTitle>
          <FormGrid>
            <InputWrap>
              <CheckboxInput
                label={'Ja'}
                value={true}
                name={'delivered_by_customer'}
              />
              <div style={{ height: '8px' }} />
              <CheckboxInput
                label={'Nej'}
                value={false}
                name={'delivered_by_customer'}
              />
            </InputWrap>
          </FormGrid>
        </InputWrap>

        {getValue('delivered_by_customer') === false ? (
          <>
            <Input
              name={'headline'}
              label={headline.title}
              placeholder={headline.placeholder}
              labelExtraText={headline.text}
              required={headline.required}
            />

            <TextareaInput
              name="date_time_and_place"
              label={dateTimePlace.title}
              placeholder={dateTimePlace.placeholder}
              labelExtraText={dateTimePlace.text}
              required={dateTimePlace.required}
            />

            <Input
              name="url"
              label={websiteLink.title}
              placeholder={websiteLink.placeholder}
              labelExtraText={websiteLink.text}
              required={websiteLink.required}
              type="url"
            />

            <TextareaInput
              name="content"
              label={textForAdd.title}
              placeholder={textForAdd.placeholder}
              labelExtraText={textForAdd.text}
              required={textForAdd.required}
            />
          </>
        ) : null}

        <FileInput
          label={image.title}
          name="image"
          required={!!getValue('delivered_by_customer')}
          restUrl={adRestImageUrl}
          text={image.text}
          placeholder={image.placeholder}
        />

        <InputWrap>
          <div className={'ad-next-step'}>
            <NextStepButton label={'Videre'} style={'secondary'} />
          </div>
        </InputWrap>
      </FormGrid>
    </>
  )
}